import React, { useEffect, useRef, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import useTranslation from "../../i18n/useTranslation";

const VERIFY_EMAIL_MUTATION = gql`
    mutation VerifyAccount($token: String!) {
        verifyAccount(token: $token) {
            success
            errors
        }
    }
`;

const VerifyEmail = () => {
    const { t } = useTranslation();
    const [status, setStatus] = useState(t("verifyEmail.status.verifying"));
    const [success, setSuccess] = useState(null);

    const params = useParams();
    const token = params.token;

    const [verifyAccount] = useMutation(VERIFY_EMAIL_MUTATION);
    const hasFetched = useRef(false);
    const isMobileBrowser =
        typeof navigator !== "undefined" &&
        /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const openMobileApp = () => {
        window.location.href = "kbolasport://email-verified";
    };

    useEffect(() => {
        const prevTitle = document.title;
        document.title = t("verifyEmail.meta.title");

        return () => {
            document.title = prevTitle;
        };
    }, [t]);

    useEffect(() => {
        if (token && !hasFetched.current) {
            hasFetched.current = true;

            verifyAccount({ variables: { token } })
                .then((res) => {
                    if (res?.data?.verifyAccount?.success) {
                        setSuccess(true);
                        setStatus(t("verifyEmail.status.verifiedSuccess"));
                    } else {
                        const errors = res?.data?.verifyAccount?.errors || {};
                        const errorMessages =
                            Object.values(errors)
                                .flat()
                                .map((err) => err.message)
                                .join("\n") || t("verifyEmail.status.unknownError");

                        const alreadyVerified =
                            errorMessages === "Account already verified.";

                        setSuccess(alreadyVerified);
                        setStatus(t("verifyEmail.status.accountAlreadyVerified"));
                    }
                })
                .catch(() => {
                    setSuccess(false);
                    setStatus(t("verifyEmail.status.genericError"));
                });
        }
    }, [token, verifyAccount, t]);

    return (
        <div
            style={{
                backgroundColor: "#14151a",
                color: "#ffffff",
                fontFamily: "'Poppins', sans-serif",
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflowX: "hidden",
                padding: "20px",
            }}
        >
            <div
                style={{
                    backgroundColor: "#1f1f2e",
                    borderRadius: "12px",
                    padding: "30px",
                    boxShadow: "0px 0px 15px rgba(0, 0, 0, 0.3)",
                    textAlign: "center",
                    maxWidth: "450px",
                    width: "100%",
                }}
            >
                <div className="mb-3">
                    <NavLink to="/">
                        <img
                                src="/dist/img/angeonx-sports.png"
                            alt={t("verifyEmail.brand.logoAlt")}
                            className="logo img-fluid"
                            style={{ height: "80px", marginBottom: "10px" }}
                        />
                    </NavLink>

                    <h2
                        style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: "var(--kbs-primary, #00adff)",
                        }}
                    >
                        {t("verifyEmail.brand.namePrimary")}{" "}
                        <span style={{ color: "#ffffff" }}>
                            {t("verifyEmail.brand.nameSecondary")}
                        </span>
                    </h2>
                </div>

                <h3
                    className="mb-3"
                    style={{ fontSize: "20px", fontWeight: "bold" }}
                >
                    {t("verifyEmail.content.title")}
                </h3>

                <p>
                    {success === true ? (
                        <FaCheckCircle size={50} color="var(--kbs-primary, #00adff)" />
                    ) : success === false ? (
                        <FaExclamationTriangle size={50} color="red" />
                    ) : null}
                </p>

                <p
                    className="mt-3"
                    style={{
                        fontSize: "16px",
                        fontWeight: "500",
                        color: "#f1f2f5",
                        whiteSpace: "pre-line",
                    }}
                >
                    {status}
                </p>

                {success === true && (
                    <>
                        <p
                            className="mt-3"
                            style={{
                                fontSize: "16px",
                                fontWeight: "500",
                                color: "#f1f2f5",
                            }}
                        >
                            {t("verifyEmail.content.readyForSignIn")}
                        </p>

                        <div
                            className="mt-4"
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                            }}
                        >
                            <button
                                type="button"
                                onClick={openMobileApp}
                                style={{
                                    backgroundColor: isMobileBrowser
                                        ? "var(--kbs-primary, #00adff)"
                                        : "transparent",
                                    color: isMobileBrowser ? "#14151a" : "#ffffff",
                                    border: "1px solid var(--kbs-primary, #00adff)",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontWeight: "bold",
                                    padding: "12px 16px",
                                }}
                            >
                                {t("verifyEmail.actions.openApp")}
                            </button>

                            <NavLink
                                to="/login"
                                style={{
                                    backgroundColor: isMobileBrowser
                                        ? "transparent"
                                        : "var(--kbs-primary, #00adff)",
                                    color: isMobileBrowser ? "#ffffff" : "#14151a",
                                    border: "1px solid var(--kbs-primary, #00adff)",
                                    borderRadius: "8px",
                                    display: "block",
                                    fontWeight: "bold",
                                    padding: "12px 16px",
                                    textDecoration: "none",
                                }}
                            >
                                {t("verifyEmail.actions.continueWeb")}
                            </NavLink>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default VerifyEmail;
